import { useState } from 'react';
import { Toaster } from 'sonner';
import { LogOut, Mail, Shield, Activity } from 'lucide-react';
import EmailDeliverabilityPage from './pages/EmailDeliverabilityPage';
import BulkEmailManagement from './components/BulkEmailManagement';
import SuperAdminPage from './pages/SuperAdminPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import ThemeToggle from './components/ThemeToggle';
import { Button } from './ui/button';

type AppView = 'bulk-email' | 'deliverability' | 'admin';

function ToolsShell() {
  const { user, loading, logout } = useAuth();
  const [view, setView] = useState<AppView>('bulk-email');

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 text-center text-sm text-muted-foreground">
        Checking your session…
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-background">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-3  sm:px-6  lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3 ">
            <img
              src="/logo.png"
              alt="68M Holidays"
              className="h-12 w-auto shrink-0 sm:h-16 rounded-lg"
            />
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Internal workspace
              </p>
              <h1 className="truncate text-lg font-bold text-foreground sm:text-xl">
                68M Holidays
              </h1>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center lg:w-auto">
            <Button
              variant={view === 'bulk-email' ? 'default' : 'outline'}
              size="sm"
              className="w-full justify-center sm:w-auto"
              onClick={() => setView('bulk-email')}
            >
              <Mail className="mr-2 h-4 w-4" />
              Bulk email
            </Button>
            <Button
              variant={view === 'deliverability' ? 'default' : 'outline'}
              size="sm"
              className="w-full justify-center sm:w-auto"
              onClick={() => setView('deliverability')}
            >
              <Activity className="mr-2 h-4 w-4" />
              Deliverability
            </Button>
            {user.isSuperAdmin ? (
              <Button
                variant={view === 'admin' ? 'default' : 'outline'}
                size="sm"
                className="w-full justify-center sm:w-auto"
                onClick={() => setView('admin')}
              >
                <Shield className="mr-2 h-4 w-4" />
                Users & access
              </Button>
            ) : null}
            <p className="truncate text-xs text-muted-foreground sm:hidden">{user.email}</p>
            <p className="hidden max-w-xs truncate text-sm text-muted-foreground lg:block lg:ml-2">
              {user.email}
            </p>
            <ThemeToggle />
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-center sm:w-auto"
              onClick={() => void logout()}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl overflow-x-hidden px-3 py-4 sm:px-6 sm:py-6">
        {view === 'admin' && user.isSuperAdmin ? (
          <SuperAdminPage />
        ) : view === 'deliverability' ? (
          <EmailDeliverabilityPage />
        ) : (
          <BulkEmailManagement />
        )}
      </main>
      <Toaster richColors position="top-center" closeButton />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ToolsShell />
    </AuthProvider>
  );
}
